import { useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import { Contract } from '@ethersproject/contracts';
import { MAX_VALUE } from '../constants';
import { callNotification } from '../notification';
import { parseErrorToUserReadableMessage } from '../format';

/**
 * Универсальный хук для работы с approve токенов
 * @param {string} tokenAddress - адрес токена
 * @param {string} spenderAddress - адрес контракта, которому нужен approve
 * @param {array} tokenAbi - ABI токена (опционально, по умолчанию ERC20)
 */
export const useApprove = (tokenAddress, spenderAddress, tokenAbi = null) => {
  const { account, provider } = useWeb3React();
  const [isApproved, setIsApproved] = useState(false);
  const [isLoadingApprove, setIsLoadingApprove] = useState(false);
  const [allowance, setAllowance] = useState(0);

  // Стандартный ERC20 ABI для approve и allowance
  const defaultERC20Abi = [
    'function approve(address spender, uint256 amount) public returns (bool)',
    'function allowance(address owner, address spender) public view returns (uint256)',
  ];

  const getTokenContract = () => {
    if (!tokenAddress || !provider) return null;

    const abi = tokenAbi || defaultERC20Abi;
    return new Contract(tokenAddress, abi, provider.getSigner(account).connectUnchecked() || provider);
  };

  /**
   * Проверка текущего allowance
   * @param {number} requiredAmount - минимальная требуемая сумма (опционально)
   */
  const checkApprove = async (requiredAmount = null) => {
    try {
      const contract = getTokenContract();
      if (!contract || !account || !spenderAddress) {
        setIsApproved(false);
        return false;
      }

      const approveBalance = await contract.allowance(account, spenderAddress);
      const allowanceValue = BigInt(approveBalance.toString());

      setAllowance(allowanceValue);

      const minRequired = requiredAmount ? BigInt(requiredAmount) : BigInt(parseInt(MAX_VALUE, 16));

      const isAllowed = allowanceValue >= minRequired;
      setIsApproved(isAllowed);

      return isAllowed;
    } catch (e) {
      console.error('Check approve error:', e);
      setIsApproved(false);
      return false;
    }
  };

  /**
   * Выполнение approve
   * @param {string|number} amount - сумма для approve (по умолчанию MAX_VALUE)
   */
  const callApprove = async (amount = MAX_VALUE) => {
    setIsLoadingApprove(true);

    try {
      const contract = getTokenContract();
      if (!contract) {
        throw new Error('Contract not initialized');
      }

      const approveAmount = amount === MAX_VALUE ? MAX_VALUE : BigInt(amount).toString();

      const result = await contract.approve(spenderAddress, approveAmount);

      callNotification({
        type: 'info',
        message: 'Transaction was sent. Please wait',
        autoClose: 10000,
      });

      await result.wait();

      setIsApproved(true);
      await checkApprove();

      callNotification({
        type: 'success',
        message: 'Approve successful',
      });

      return result;
    } catch (e) {
      console.error('Approve error:', e);
      setIsApproved(false);
      callNotification({
        type: 'error',
        message: parseErrorToUserReadableMessage(e),
      });
      return Promise.reject(e);
    } finally {
      setIsLoadingApprove(false);
    }
  };

  /**
   * Проверяет approve и автоматически делает его, если недостаточно
   * @param {number} requiredAmount - требуемая сумма для проверки
   * @param {string|number} approveAmount - сумма для approve (по умолчанию MAX_VALUE)
   * @returns {Promise<boolean>} true если approve есть или успешно выполнен
   */
  const ensureApprove = async (requiredAmount = null, approveAmount = MAX_VALUE) => {
    try {
      // Проверяем текущий approve
      const hasApprove = await checkApprove(requiredAmount);

      if (hasApprove) {
        return true;
      }

      // Если approve недостаточно - делаем новый approve
      await callApprove(approveAmount);
      return true;
    } catch (e) {
      console.error('Ensure approve error:', e);
      return false;
    }
  };

  return {
    isApproved,
    isLoadingApprove,
    allowance,
    checkApprove,
    callApprove,
    ensureApprove,
  };
};
